import {
	ContentExpander,
	ErrorLabel,
	Grid,
	Label,
	NumberField,
	Radio,
	Spacer
} from '@components';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
	AllRateCardFuelLevyModifiers, AllRateCardSettingFuelLevyModifiers,
	RateCardFormErrors,
	RateCardSettingsTypes, RateCardTypes
} from '@state';
import {
	RateCardType,
	TRateCardModifierSet,
	TRateCardModifierType
} from '@types';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { useRecoilValue } from 'recoil';
import styles from './Modifiers.module.scss';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType;
	isDisabled?: boolean;
	id?: string;
}

export const DGModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		id
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		id,
		isSettings: !!isSettings,
		type
	});
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateAdditionalStopsModifier = (
		key: 'type' | 'value' | 'threshold_kg' | 'value_below' | 'value_above',
		newValue: string
	) => {
		let newDG: TRateCardModifierSet['dangerous_goods'] = null;

		if (key === 'type') {
			if (newValue === TRateCardModifierType.THRESHOLD_KG) {
				newDG = {
					type: TRateCardModifierType.THRESHOLD_KG,
					value_above: '',
					value_below: '',
					threshold_kg: '',
				}
			} else {
				newDG = {
					type: newValue as TRateCardModifierType.FIXED | TRateCardModifierType.PERCENT,
					value: ''
				}
			}
		} else if (modifiers?.dangerous_goods?.type) {
			newDG = {
				...modifiers?.dangerous_goods,
				[key]: newValue
			}
		}

		if (newValue === 'None') {
			newDG = null;
		}

		const update = {
			...modifiers,
			dangerous_goods: newDG ? {
				...newDG,
				attracts_fuel_levy: !!fuelLevyModifiers?.dangerous_goods?.attracts_fuel_levy
			} : null
		} as TRateCardModifierSet;

		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardModifiers(newModifiers);
		const errorsArray = [`${errorString}.${rateCardTypeIndex}.modifier_set.dangerous_goods.${key}`]
		if (key === 'value_above' || key === 'value_below' || key === 'type') {
			errorsArray.push(`${errorString}.${rateCardTypeIndex}.modifier_set.dangerous_goods.fees`)
		}
		clearRCSettingsError(errorsArray)
	}

	const handleOnBlur = (
		key: 'type' | 'value' | 'threshold_kg' | 'value_below' | 'value_above',
		newValue: string
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateAdditionalStopsModifier(key, value);
		}
		if (newValue.startsWith('.')) {
			updateAdditionalStopsModifier(key, `0${newValue}`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.dangerous_goods?.[key]?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = ['value', 'threshold_kg', 'value_below', 'value_above', 'fees']
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	const getConfiguredCount = () => {
		const dg = modifiers?.dangerous_goods;
		if (!dg) return 0;
		if (dg.type === TRateCardModifierType.THRESHOLD_KG) {
			const below = parseFloat(`${dg.value_below ?? ''}`);
			const above = parseFloat(`${dg.value_above ?? ''}`);
			return below > 0 || above > 0 ? 1 : 0;
		}
		// @ts-ignore - FIXED / PERCENT variants have a single `value` field
		return parseFloat(`${dg.value ?? ''}`) > 0 ? 1 : 0;
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Dangerous goods
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Label>Charge method</Label>
				<Radio.Group className={styles.radioContainer}>
					<Radio
						name={'charge_method'}
						label={'Flat fee'}
						value={TRateCardModifierType.FIXED}
						onChange={(e) => updateAdditionalStopsModifier('type', e.target.value)}
						checked={modifiers?.dangerous_goods?.type === TRateCardModifierType.FIXED}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'Percentage'}
						value={TRateCardModifierType.PERCENT}
						onChange={(e) => updateAdditionalStopsModifier('type', e.target.value)}
						checked={modifiers?.dangerous_goods?.type === TRateCardModifierType.PERCENT}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'Fee per break'}
						value={TRateCardModifierType.THRESHOLD_KG}
						onChange={(e) => updateAdditionalStopsModifier('type', e.target.value)}
						checked={modifiers?.dangerous_goods?.type === TRateCardModifierType.THRESHOLD_KG}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'None'}
						value={'None'}
						onChange={(e) => updateAdditionalStopsModifier('type', e.target.value)}
						checked={modifiers?.dangerous_goods === null}
						disabled={isDisabled}
					/>
				</Radio.Group>
				{
					modifiers?.dangerous_goods &&
					(
						modifiers?.dangerous_goods?.type === TRateCardModifierType.PERCENT ||
						modifiers?.dangerous_goods?.type === TRateCardModifierType.FIXED
					) &&
						<Grid columns={3}>
							<NumberField
								name={'dangerous_goods'}
								value={`${modifiers.dangerous_goods?.value ?? ''}`}
								onChange={(e) => updateAdditionalStopsModifier('value', e.target.value)}
								leftModule={<span className={styles.prefix}>{
									modifiers?.dangerous_goods?.type === TRateCardModifierType.PERCENT ? '%' :
										modifiers?.dangerous_goods?.type === TRateCardModifierType.FIXED ? '$' :
											'' /* add per rate break rate? */
								}</span>}
								label={
									modifiers?.dangerous_goods?.type === TRateCardModifierType.PERCENT ? 'Percentage of total price' :
										modifiers?.dangerous_goods?.type === TRateCardModifierType.FIXED ? 'Flat fee' :
											'' /* add per rate break rate? */
								}
								keepErrorSpace={false}
								required
								errorMessage={hasError('value')}
								hasError={!!hasError('value')}
								disabled={isDisabled}
								min={'0'}
								onBlur={(e) => handleOnBlur('value', e.target.value)}
							/>
							<Grid.Item />
							<Grid.Item />
						</Grid>
				}
				{
					modifiers?.dangerous_goods?.type === TRateCardModifierType.THRESHOLD_KG &&
						<Grid
							columns={5}
							columnTemplate='minmax(100px, 150px) 25px minmax(100px, 150px) 25px minmax(100px, 150px)'
							columnGap='8px'
							alignItems='start'
						>
							<NumberField
								name={'value_below'}
								value={`${modifiers.dangerous_goods?.value_below ?? ''}`}
								onChange={(e) => updateAdditionalStopsModifier('value_below', e.target.value)}
								leftModule={<span className={styles.prefix}>$</span>}
								label={'Fee below'}
								keepErrorSpace={false}
								errorMessage={hasError('value_below')}
								hasError={!!hasError('value_below')}
								disabled={isDisabled}
								min={'0'}
								onBlur={(e) => handleOnBlur('value_below', e.target.value)}
							/>
							<div>
								<Spacer size={35} />
								<ArrowBackIcon />
							</div>
							<NumberField
								name={'threshold_kg'}
								value={`${modifiers.dangerous_goods?.threshold_kg ?? ''}`}
								onChange={(e) => updateAdditionalStopsModifier('threshold_kg', e.target.value)}
								rightModule={<span className={styles.suffix}>kg</span>}
								label={'Break point'}
								required
								keepErrorSpace={false}
								errorMessage={hasError('threshold_kg')}
								hasError={!!hasError('threshold_kg')}
								disabled={isDisabled}
								min={'0'}
								onBlur={(e) => handleOnBlur('threshold_kg', e.target.value)}
							/>
							<div>
								<Spacer size={35} />
								<ArrowForwardIcon />
							</div>
							<NumberField
								name={'value_above'}
								value={`${modifiers.dangerous_goods?.value_above ?? ''}`}
								onChange={(e) => updateAdditionalStopsModifier('value_above', e.target.value)}
								leftModule={<span className={styles.prefix}>$</span>}
								label={'Fee above'}
								keepErrorSpace={false}
								errorMessage={hasError('value_above')}
								hasError={!!hasError('value_above')}
								disabled={isDisabled}
								min={'0'}
								onBlur={(e) => handleOnBlur('value_above', e.target.value)}
							/>
						</Grid>
				}
				<Spacer size={10} />
			</div>
			{
				!!hasError('fees') && <>
					<Spacer size={10} />
					<ErrorLabel hasError={!!hasError('fees')} errorMessage={hasError('fees')} />
				</>
			}
		</ContentExpander>
	);
}